import { Descriptions, Modal, Spin } from 'antd';
import React from 'react';
import { Button } from '../../../components/button';
import { NotificationManager } from '../../../components/notification-manager';
import { Tag } from '../../../components/tag';
import { useContestStore } from '../../../contexts/mobx';
import type {
    ContestAppTarget,
    ContestChannel,
    ContestEntryDetail,
} from '../../../stores/contest';
import { formatAppTargets } from '../../../utils/app-target';
import { convertUtcToLocalMoment } from '../../../utils/timezone';
import {
    BLOCK_TYPE_LABELS,
    CHANNEL_LABELS,
    ENTRY_PATH_LABELS,
    GRANT_SOURCE_LABELS,
    UNCOUNTED_REASON_LABELS,
} from './labels';
import style from './style.module.scss';

interface EntryDetailProps {
    contestId: number;
    /** The entry shown, or null while the modal is closed. */
    entryId: string | null;
    timeZone: string;
    onClose: () => void;
    /** A person was anonymised: the list shows them as such. */
    onAnonymized: () => void;
}

const EMPTY = '—';

function formatInstant(utc: string | null, timeZone: string): string {
    return utc
        ? convertUtcToLocalMoment(utc, timeZone)?.format('lll') ?? EMPTY
        : EMPTY;
}

function formatOrigin(origin: {
    appTarget: ContestAppTarget | null;
    channel: ContestChannel | null;
}): string {
    const parts = [
        origin.appTarget && formatAppTargets([origin.appTarget]),
        origin.channel && CHANNEL_LABELS[origin.channel],
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : EMPTY;
}

/** The photo, or why it cannot be shown: storage refused to sign it, or the browser to load it. */
const EntryPhoto = ({ url }: { url: string | null }) => {
    const [failed, setFailed] = React.useState(false);

    if (!url || failed) {
        return (
            <p className={style.photo_missing}>
                The photo could not be loaded.
            </p>
        );
    }
    return (
        <a href={url} target="_blank" rel="noopener noreferrer">
            <img
                className={style.photo}
                src={url}
                alt="Photo submitted with the entry"
                onError={() => setFailed(true)}
            />
        </a>
    );
};

/** Every value of one entry, read when it opens, with its photo. */
export const EntryDetail = ({
    contestId,
    entryId,
    timeZone,
    onClose,
    onAnonymized,
}: EntryDetailProps) => {
    const store = useContestStore();
    const [entry, setEntry] = React.useState<ContestEntryDetail | null>(null);
    const [failed, setFailed] = React.useState(false);
    // Read the entry again once its participant is anonymised.
    const [readCount, setReadCount] = React.useState(0);
    const [isAnonymizing, setIsAnonymizing] = React.useState(false);

    React.useEffect(() => {
        setEntry(null);
        setFailed(false);
        if (entryId === null) {
            return;
        }
        // A closed modal, or another entry opened, drops this answer.
        let current = true;
        store.fetchEntry(contestId, entryId).then((detail) => {
            if (current) {
                setEntry(detail);
                setFailed(detail === null);
            }
        });
        return () => {
            current = false;
        };
    }, [contestId, entryId, readCount, store]);

    const participant = entry?.participant;

    const confirmAnonymization = (participantId: string) =>
        Modal.confirm({
            title: 'Anonymise this person?',
            content:
                'Their phone, email and postal code are erased in every contest they entered with this phone, with their contact messages and their photos. Their entries stay and keep counting. This cannot be undone.',
            okText: 'Anonymise',
            okButtonProps: { danger: true },
            onOk: async () => {
                setIsAnonymizing(true);
                const report = await store.anonymizeParticipant(
                    contestId,
                    participantId
                );
                setIsAnonymizing(false);
                if (report) {
                    NotificationManager.showSuccess(
                        `Anonymised in ${report.participants} contests, with ${report.messages} messages and ${report.photos} photos`
                    );
                    setReadCount((count) => count + 1);
                    onAnonymized();
                }
            },
        });

    return (
        <Modal
            open={entryId !== null}
            onCancel={onClose}
            footer={null}
            title="Entry"
            width={720}
        >
            {failed ? (
                <p>This entry could not be loaded.</p>
            ) : !entry || !participant ? (
                <div className={style.loading}>
                    <Spin />
                </div>
            ) : (
                <>
                    <Descriptions
                        column={1}
                        size="small"
                        bordered
                        title="Participant"
                    >
                        {participant.anonymizedAt && (
                            <Descriptions.Item label="Anonymised">
                                {formatInstant(
                                    participant.anonymizedAt,
                                    timeZone
                                )}
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label="Phone">
                            {participant.phoneNumber ?? EMPTY}
                        </Descriptions.Item>
                        <Descriptions.Item label="Email">
                            {participant.email ?? EMPTY}
                        </Descriptions.Item>
                        <Descriptions.Item label="Postal code">
                            {participant.postalCode ?? EMPTY}
                        </Descriptions.Item>
                        <Descriptions.Item label="Province">
                            {participant.province}
                        </Descriptions.Item>
                        <Descriptions.Item label="Language">
                            {participant.lang.toUpperCase()}
                        </Descriptions.Item>
                        <Descriptions.Item label="Signed up">
                            {formatInstant(participant.signedUpAt, timeZone)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Signed up through">
                            {formatOrigin(participant)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Skill-testing question passed">
                            {formatInstant(participant.skillPassedAt, timeZone)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Participant ID">
                            {participant.id}
                        </Descriptions.Item>
                    </Descriptions>

                    {!participant.anonymizedAt && (
                        <Button
                            className={style.anonymize}
                            variant="secondary-destructive"
                            loading={isAnonymizing}
                            onClick={() => confirmAnonymization(participant.id)}
                        >
                            Anonymise this person
                        </Button>
                    )}

                    <Descriptions
                        className={style.entry_section}
                        column={1}
                        size="small"
                        bordered
                        title="Entry"
                    >
                        <Descriptions.Item label="Date">
                            {formatInstant(entry.createdAt, timeZone)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Source">
                            {GRANT_SOURCE_LABELS[entry.grantSource]}
                        </Descriptions.Item>
                        <Descriptions.Item label="Block">
                            {entry.block
                                ? `${entry.block.key} (${
                                      BLOCK_TYPE_LABELS[entry.block.type]
                                  })`
                                : EMPTY}
                        </Descriptions.Item>
                        <Descriptions.Item label="Block revision">
                            {entry.blockRevisionId ?? EMPTY}
                        </Descriptions.Item>
                        <Descriptions.Item label="Submitted through">
                            {formatOrigin(entry)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Entry path">
                            {ENTRY_PATH_LABELS[entry.entryPath]}
                        </Descriptions.Item>
                        <Descriptions.Item label="Proof of purchase">
                            {entry.proofValue ?? EMPTY}
                        </Descriptions.Item>
                        <Descriptions.Item label="Chances">
                            {entry.quantity}
                            {entry.uncountedReason && (
                                <Tag variant="grey" className={style.reason}>
                                    {
                                        UNCOUNTED_REASON_LABELS[
                                            entry.uncountedReason
                                        ]
                                    }
                                </Tag>
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Entry ID">
                            {entry.id}
                        </Descriptions.Item>
                    </Descriptions>

                    {entry.photo && (
                        <EntryPhoto key={entry.id} url={entry.photo.url} />
                    )}
                </>
            )}
        </Modal>
    );
};
